import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { RoomInterface, SpaceRoomContentDtoInterface } from '@simOn/room/models';
import { Observable, combineLatest, filter, map, switchMap } from 'rxjs';
import {
  AddRoom,
  AddRoomFailure,
  AddRoomSuccess,
  ClearRoomsState,
  DeleteRoom,
  DeleteRoomFailure,
  DeleteRoomSuccess,
  GetRoom,
  GetRooms,
  SetRoomCamera,
  SetRoomCameraFailure,
  SetRoomCameraSuccess,
  SetSelectedId,
  UpdateRoom,
  UpdateRoomFailure,
  UpdateRoomOnStore,
  UpdateRoomSuccess,
  UpdateSweepInRoom
} from './room.actions';
import {
  GetSelectedId,
  MasterRoomsLoaded,
  RoomName,
  SelectAllRoomsWithoutMaster,
  SelectMasterRoom,
  SelectRoomContentById,
  SelectRooms,
  roomsLoaded,
  selectRoomById,
  selectRoomNameById,
  selectRoomsState,
  selectedRoomId
} from './room.selectors';

@Injectable({
  providedIn: 'root'
})
export class RoomFacade {
  private readonly store = inject(Store);
  private readonly action = inject(Actions);
  readonly masterRoom$: Observable<RoomInterface | undefined> = this.store.select(SelectMasterRoom);
  readonly masterRoomsLoaded$: Observable<boolean> = this.store
    .select(MasterRoomsLoaded)
    .pipe(filter((loaded) => loaded));

  readonly selectedId$: Observable<string | undefined> = this.store.select(GetSelectedId);
  readonly rooms$: Observable<RoomInterface[]> = this.store.select(SelectRooms);
  readonly roomNames$: Observable<string[]> = this.store.select(SelectRooms).pipe(
    map((rooms) => {
      return rooms.map((room) => room.name!);
    })
  );

  readonly roomsEntity$ = this.store.select(selectRoomsState);
  readonly roomLoaded$: Observable<boolean> = this.store.select(roomsLoaded).pipe(
    map((loaded) => loaded || false),
    filter((loaded) => loaded)
  );
  readonly roomsWithoutMaster$: Observable<RoomInterface[]> = this.roomLoaded$.pipe(
    switchMap(() => this.store.select(SelectAllRoomsWithoutMaster))
  );
  readonly selectedRoomId$: Observable<string | undefined> = this.store.select(selectedRoomId);
  readonly selectAllRoomsWithMasterRoom$: Observable<RoomInterface[]> = combineLatest([
    this.masterRoomsLoaded$,
    this.roomLoaded$
  ]).pipe(switchMap(() => this.store.select(SelectRooms)));
  readonly addRoomActionFinish$ = this.action.pipe(ofType(AddRoomSuccess, AddRoomFailure));
  readonly updateRoomActionFinish$ = this.action.pipe(ofType(UpdateRoomSuccess, UpdateRoomFailure));
  readonly deleteRoomActionFinish$ = this.action.pipe(ofType(DeleteRoomSuccess, DeleteRoomFailure));
  readonly setCameraAction$ = this.action.pipe(ofType(SetRoomCameraFailure, SetRoomCameraSuccess));
  roomById$ = (roomId: string): Observable<RoomInterface> => this.store.select(selectRoomById(roomId));
  selectRoomNameById$ = (roomId: string): Observable<string> => this.store.select(selectRoomNameById(roomId));

  getRoomContentById$ = (roomId: string, id: string): Observable<SpaceRoomContentDtoInterface | undefined> =>
    this.store.select(SelectRoomContentById(roomId, id));
  readonly roomName$ = (roomGuid: string) => this.store.select(RoomName(roomGuid));

  setSelectedId(selectedId: string): void {
    this.store.dispatch(SetSelectedId({ selectedId }));
  }

  getRooms(): void {
    this.store.dispatch(GetRooms());
  }

  getRoom(roomId: string): void {
    this.store.dispatch(GetRoom({ state: roomId }));
  }

  addRoom(room: RoomInterface): void {
    this.store.dispatch(AddRoom({ state: room }));
  }

  updateRoom(room: RoomInterface): void {
    this.store.dispatch(UpdateRoom({ room }));
  }

  updateRoomOnStore(room: RoomInterface): void {
    this.store.dispatch(UpdateRoomOnStore({ room }));
  }

  updateSweepInRoom(roomId: string, scanModelId: string, sweepId: string): void {
    this.store.dispatch(UpdateSweepInRoom({ roomId, scanModelId, sweepId }));
  }

  deleteRoom(roomId: string): void {
    this.store.dispatch(DeleteRoom({ state: roomId }));
  }

  clearState() {
    this.store.dispatch(ClearRoomsState());
  }
  setRoomCamera(payload: {
    roomId: string;
    roomCamera: {
      scanModelId: string;
      position: import('libs/simlab-matterport/assets/mpSdk').Vector3;
      rotation: import('libs/simlab-matterport/assets/mpSdk').Vector2;
    };
  }) {
    this.store.dispatch(SetRoomCamera(payload));
  }
}
